if (!customElements.get('byte-rich-text-read-more')) {
  class ByteRichTextReadMore extends HTMLElement {
    static EXPAND_MS = 260;
    static COLLAPSE_MS = 180;
    static EXPAND_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';
    static COLLAPSE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

    #boundResize = null;
    #boundSectionLoad = null;
    #boundDetailsToggle = null;
    #detailsEl = null;
    #toggleClick = null;
    #resizeTimer = null;

    connectedCallback() {
      this.innerEl = this.querySelector('[data-read-more-content]');
      this.toggleBtn = this.querySelector('[data-read-more-toggle]');
      if (!this.innerEl || !this.toggleBtn) return;

      this.#toggleClick = () => this.#onToggle();
      this.toggleBtn.addEventListener('click', this.#toggleClick);

      this.#boundResize = () => {
        clearTimeout(this.#resizeTimer);
        this.#resizeTimer = setTimeout(() => this.#scheduleUpdate(), 150);
      };
      window.addEventListener('resize', this.#boundResize);

      this.#detailsEl = this.closest('details');
      if (this.#detailsEl) {
        this.#boundDetailsToggle = () => this.#scheduleUpdate();
        this.#detailsEl.addEventListener('toggle', this.#boundDetailsToggle);
      }

      if (window.Shopify?.designMode) {
        this.#boundSectionLoad = () => this.#scheduleUpdate();
        document.addEventListener('shopify:section:load', this.#boundSectionLoad);
      }

      this.#scheduleUpdate();
    }

    disconnectedCallback() {
      clearTimeout(this.#resizeTimer);
      this.toggleBtn?.removeEventListener('click', this.#toggleClick);
      window.removeEventListener('resize', this.#boundResize);
      this.#detailsEl?.removeEventListener('toggle', this.#boundDetailsToggle);
      document.removeEventListener('shopify:section:load', this.#boundSectionLoad);
    }

    #scheduleUpdate() {
      requestAnimationFrame(() => this.#update());
    }

    #prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    #getCollapsedMaxPx() {
      const styles = getComputedStyle(this);
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const raw = isDesktop
        ? styles.getPropertyValue('--rm-desktop').trim()
        : styles.getPropertyValue('--rm-mobile').trim();
      const parsed = parseInt(raw, 10);
      return Number.isFinite(parsed) ? parsed : isDesktop ? 120 : 100;
    }

    #syncToggleUi(expanded) {
      this.toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.toggleBtn.textContent = expanded ? this.toggleBtn.dataset.labelLess : this.toggleBtn.dataset.labelMore;
    }

    #clearInnerMotionStyles() {
      this.innerEl.style.maxHeight = '';
      this.innerEl.style.overflow = '';
      this.innerEl.style.transition = '';
    }

    #onToggle() {
      if (this.classList.contains('is-animating')) return;

      if (this.#prefersReducedMotion()) {
        const expanded = this.classList.toggle('is-expanded');
        this.#clearInnerMotionStyles();
        this.#syncToggleUi(expanded);
        return;
      }

      if (this.classList.contains('is-expanded')) {
        this.#collapseAnimated();
      } else {
        this.#expandAnimated();
      }
    }

    #expandAnimated() {
      const collapsedPx = this.#getCollapsedMaxPx();
      const fullHeight = this.innerEl.scrollHeight;

      if (fullHeight <= collapsedPx + 2) {
        this.classList.add('is-expanded');
        this.#syncToggleUi(true);
        return;
      }

      let done = false;
      const durationMs = ByteRichTextReadMore.EXPAND_MS;

      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(fallbackTimer);
        this.innerEl.removeEventListener('transitionend', onEnd);
        this.classList.add('is-expanded');
        this.classList.remove('is-animating', 'is-opening');
        this.#clearInnerMotionStyles();
        this.#syncToggleUi(true);
      };

      const onEnd = (e) => {
        if (e.propertyName !== 'max-height') return;
        finish();
      };

      this.classList.add('is-animating', 'is-opening');

      this.innerEl.style.overflow = 'hidden';
      this.innerEl.style.maxHeight = `${collapsedPx}px`;
      this.innerEl.style.transition = `max-height ${durationMs}ms ${ByteRichTextReadMore.EXPAND_EASING}`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.innerEl.style.maxHeight = `${fullHeight}px`;
        });
      });

      const fallbackTimer = setTimeout(finish, durationMs + 120);
      this.innerEl.addEventListener('transitionend', onEnd);
    }

    #collapseAnimated() {
      let done = false;
      const collapsedPx = this.#getCollapsedMaxPx();
      const fullHeight = this.innerEl.scrollHeight;
      const durationMs = ByteRichTextReadMore.COLLAPSE_MS;

      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(fallbackTimer);
        this.innerEl.removeEventListener('transitionend', onEnd);
        this.classList.remove('is-expanded', 'is-animating');
        this.#clearInnerMotionStyles();
        this.#syncToggleUi(false);
      };

      const onEnd = (e) => {
        if (e.propertyName !== 'max-height') return;
        finish();
      };

      this.classList.add('is-animating');

      this.innerEl.style.maxHeight = `${fullHeight}px`;
      this.innerEl.style.overflow = 'hidden';
      this.innerEl.style.transition = `max-height ${durationMs}ms ${ByteRichTextReadMore.COLLAPSE_EASING}`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.innerEl.style.maxHeight = `${collapsedPx}px`;
        });
      });

      const fallbackTimer = setTimeout(finish, durationMs + 120);
      this.innerEl.addEventListener('transitionend', onEnd);
    }

    #update() {
      if (this.classList.contains('is-animating')) return;

      const fullHeight = this.innerEl.scrollHeight;
      const maxPx = this.#getCollapsedMaxPx();
      const needsClamp = fullHeight > maxPx + 2;

      this.classList.toggle('no-clamp', !needsClamp);

      if (!needsClamp) {
        this.classList.remove('is-expanded');
        this.#clearInnerMotionStyles();
        this.toggleBtn.hidden = true;
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.toggleBtn.textContent = this.toggleBtn.dataset.labelMore;
        return;
      }

      this.toggleBtn.hidden = false;

      if (!this.classList.contains('is-expanded')) {
        this.#clearInnerMotionStyles();
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.toggleBtn.textContent = this.toggleBtn.dataset.labelMore;
      } else {
        this.#clearInnerMotionStyles();
        this.toggleBtn.setAttribute('aria-expanded', 'true');
        this.toggleBtn.textContent = this.toggleBtn.dataset.labelLess;
      }
    }
  }

  customElements.define('byte-rich-text-read-more', ByteRichTextReadMore);
}
